<script setup>
/* eslint-disable-next-line lines-around-comment -- page docblock is the first thing in the file on purpose */
/*
  The bedside AI concierge, on its own route.

  It used to share `/service-desk` with the public complaint form, told apart
  only by the presence of `?t=`. Two unrelated products behind one path meant
  every consumer had to re-derive which one it was looking at -- the client
  layout, the service worker's tab matcher, the QR generator. They are
  separate paths now, and the form keeps the bare `/service-desk` because its
  legacy `?s=&l=&u=` codes are printed and in the field, while the
  scan-to-concierge flow was never launched.
*/
definePage({
  meta: {
    layout: 'client',
    public: true,
    unauthenticatedOnly: true,
    title: 'Bedside Chat',

    // Read by layouts/client.vue: this page is a full-bleed, full-height chat
    // that owns the whole viewport, so the layout drops its navbar and footer.
    // Declared here rather than matched on the path there, so moving this
    // route again needs no edit to the layout.
    fullBleed: true,
  },
})

const route = useRoute()

// The bed's QR token. This route has no meaning without it.
const qrToken = computed(() => route.query.t ?? null)

// Set by the service worker when a resolved-request notification is tapped
// with no chat tab already open. The warm path (a tab that only gets focused)
// cannot use a query string at all and arrives by postMessage instead.
const rateTicket = computed(() => route.query.rate ?? null)

// A tokenless visit never reaches this component -- the router guard sends it
// to /invalid-url first. The v-if below is the belt to that braces.
</script>

<template>
  <!--
    Suspense here (not just the ancestor one in layouts/client.vue) is
    required: an async component (top-level awaits) nested a level below a
    synchronous wrapper is not picked up correctly by an ancestor Suspense
    alone in this Vue 3.3.8 setup -- it must be the direct child of a
    Suspense boundary. Verified in the browser on the sibling route: without
    it the page renders blank with a Suspense-internal TypeError.
  -->
  <Suspense>
    <ConciergeChat
      v-if="qrToken"
      :qr-token="qrToken"
      :rate-ticket="rateTicket"
    />
  </Suspense>
</template>
